import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { GEN_LABELS } from "../i18n";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";

const GENS = ["genx", "geny", "genz", "genalpha"];

export default function TestPage() {
  const navigate = useNavigate();
  const { t, lang, user } = useAuth();
  const [params] = useSearchParams();
  const sessionCode = params.get("session");

  const [phase, setPhase] = useState("setup"); // setup | quiz | submitting
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    generation: "geny",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  useEffect(() => {
    api.get("/questions").then(({ data }) => setQuestions(data.questions));
  }, []);

  const start = (e) => {
    e.preventDefault();
    setPhase("quiz");
  };

  const choose = (q, pole) => {
    const next = { ...answers, [q.id]: pole };
    setAnswers(next);
    setTimeout(() => {
      if (idx < questions.length - 1) setIdx(idx + 1);
      else finish(next);
    }, 180);
  };

  const finish = async (finalAnswers) => {
    setPhase("submitting");
    try {
      const payload = {
        name: form.name,
        email: form.email || null,
        language: lang,
        generation: form.generation,
        answers: Object.entries(finalAnswers).map(([qid, pole]) => ({
          question_id: Number(qid),
          pole,
        })),
        session_code: sessionCode || null,
      };
      const { data } = await api.post("/test/submit", payload);
      navigate(`/result/${data.result_id}`);
    } catch (err) {
      setPhase("quiz");
      alert("Submission failed. Please try again.");
    }
  };

  // ---- submitting screen
  if (phase === "submitting") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <Sparkles className="gold-text mb-6" size={40} />
        <Loader2 className="animate-spin gold-text mb-6" size={32} />
        <h2 className="font-heading text-2xl" style={{ color: "var(--text)" }}>{t("submitting")}</h2>
        <p className="mt-2 text-sm" style={{ color: "var(--text-dim)" }}>{t("please_wait")}</p>
      </div>
    );
  }

  // ---- setup screen
  if (phase === "setup") {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="max-w-2xl mx-auto px-5 sm:px-8 py-14 animate-fade-up" style={{ opacity: 0 }}>
          <h1 className="font-heading text-4xl mb-2" style={{ color: "var(--text)" }}>{t("setup_title")}</h1>
          <p className="text-sm mb-10" style={{ color: "var(--text-dim)" }}>{t("setup_sub")}</p>

          {sessionCode && (
            <div className="card-amt p-4 mb-6 text-sm" style={{ color: "var(--gold)" }} data-testid="session-badge">
              {t("join_code")}: <b>{sessionCode}</b>
            </div>
          )}

          <form onSubmit={start} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("label_name")}</label>
              <input data-testid="setup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-amt w-full px-4 py-3 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("label_email")}</label>
              <input data-testid="setup-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-amt w-full px-4 py-3 mt-1" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block" style={{ color: "var(--text-dim)" }}>{t("label_generation")}</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {GENS.map((g) => (
                  <button
                    key={g}
                    type="button"
                    data-testid={`gen-${g}`}
                    onClick={() => setForm({ ...form, generation: g })}
                    className="py-3 rounded-sm text-sm font-semibold border transition-colors"
                    style={{
                      borderColor: form.generation === g ? "var(--gold)" : "var(--border)",
                      background: form.generation === g ? "rgba(201,165,87,0.12)" : "transparent",
                      color: form.generation === g ? "var(--gold)" : "var(--text-dim)",
                    }}
                  >
                    {GEN_LABELS[g]}
                  </button>
                ))}
              </div>
            </div>
            <button data-testid="setup-start-btn" className="btn-gold w-full py-3.5 inline-flex items-center justify-center gap-2">
              {t("start_test")} <ArrowRight size={18} />
            </button>
          </form>
        </main>
      </div>
    );
  }

  // ---- quiz screen
  const q = questions[idx];
  if (!q) return null;
  const progress = ((idx) / questions.length) * 100;

  return (
    <div className="min-h-screen">
      {/* progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1" style={{ background: "var(--border)" }}>
        <div
          data-testid="progress-bar"
          className="h-full transition-all duration-300"
          style={{ width: `${progress}%`, background: "var(--gold)" }}
        />
      </div>

      <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-20 pb-16">
        <div key={q.id} className="animate-fade-in">
          <p className="text-xs uppercase tracking-[0.25em] mb-8" style={{ color: "var(--gold)" }} data-testid="question-counter">
            {t("question")} {idx + 1} {t("of")} {questions.length}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl leading-snug mb-10" style={{ color: "var(--text)" }} data-testid="question-text">
            {q.text[lang]}
          </h2>

          <div className="space-y-4">
            {["a", "b"].map((opt) => {
              const o = q[opt];
              const active = answers[q.id] === o.pole;
              return (
                <button
                  key={opt}
                  data-testid={`option-${opt}`}
                  onClick={() => choose(q, o.pole)}
                  className="w-full text-left p-5 rounded-sm border transition-all"
                  style={{
                    borderColor: active ? "var(--gold)" : "var(--border)",
                    background: active ? "rgba(201,165,87,0.12)" : "var(--surface)",
                    color: "var(--text)",
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = "rgba(201,165,87,0.5)"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = "var(--border)"; }}
                >
                  <span className="font-medium">{o.text[lang]}</span>
                </button>
              );
            })}
          </div>

          {idx > 0 && (
            <button
              data-testid="prev-btn"
              onClick={() => setIdx(idx - 1)}
              className="mt-8 inline-flex items-center gap-2 text-sm"
              style={{ color: "var(--text-dim)" }}
            >
              <ArrowLeft size={16} /> {t("prev")}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
